<script setup>
import { computed, nextTick, provide, ref, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useReferenceLayout } from "@/composables/useReferenceLayout";
import ReferenceTabNavigation from "@/components/reference/ReferenceTabNavigation.vue";
import { useReferenceController } from "@/composables/useReferenceController";
import { referenceWorkspaceKey } from "@/components/reference/referenceWorkspace";
import ReferenceClassicNavigation from "@/components/reference/ReferenceClassicNavigation.vue";
import ReferenceHeader from "@/components/reference/ReferenceHeader.vue";
import ReferenceDialogs from "@/components/reference/ReferenceDialogs.vue";
import ReferenceProgressSection from "@/components/reference/ReferenceProgressSection.vue";
import ReferenceOverviewSections from "@/components/reference/ReferenceOverviewSections.vue";
import ReferenceJourneySection from "@/components/reference/ReferenceJourneySection.vue";
import ReferenceScheduleSection from "@/components/reference/ReferenceScheduleSection.vue";
import ReferenceAllocationSection from "@/components/reference/ReferenceAllocationSection.vue";
import ReferencePayrollSection from "@/components/reference/ReferencePayrollSection.vue";
import ReferenceTextSection from "@/components/reference/ReferenceTextSection.vue";
import ReferenceActivitySections from "@/components/reference/ReferenceActivitySections.vue";

const workspace = useReferenceController();
provide(referenceWorkspaceKey, workspace);
const {
  reference,
  canManageSchedule,
  canViewPayrollClaims,
  sectionEditing,
  resourceAllocationEditing,
  isResourceDirty,
  resourceSaveError,
  resourceDraftRecovery,
} = workspace;
const route = useRoute();
const router = useRouter();
const { isClassic } = useReferenceLayout();
const requestedTab = ref(route.query?.tab ?? "overview");
const tabs = computed(() => [
  {
    id: "overview",
    label: "Overview",
    notice: ["overview", "locations", "description", "notes"].some(
      (id) => sectionEditing[id],
    )
      ? "Editing"
      : "",
  },
  ...(canManageSchedule.value || reference.data?.platform365_journey
    ? [
        {
          id: "schedule",
          label: "Schedule",
          notice: resourceSaveError.value
            ? "Save failed"
            : isResourceDirty.value
              ? "Unsaved"
              : resourceDraftRecovery.value
                ? "Draft available"
                : "",
        },
      ]
    : []),
  {
    id: "resources",
    label: "Resources",
    notice: resourceAllocationEditing.value ? "Editing" : "",
  },
  { id: "activity", label: "Activity" },
  ...(canViewPayrollClaims.value ? [{ id: "payroll", label: "Payroll" }] : []),
]);
const activeTab = computed(() =>
  tabs.value.some((tab) => tab.id === requestedTab.value)
    ? requestedTab.value
    : "overview",
);
const activeTabLabel = computed(
  () => tabs.value.find((tab) => tab.id === activeTab.value)?.label,
);
const showTab = (id) => isClassic.value || activeTab.value === id;
watch(
  () => route.query?.tab,
  (value) => {
    requestedTab.value = value ?? "overview";
  },
);
const selectTab = async (id) => {
  if (activeTab.value === id) return;
  requestedTab.value = id;
  await router.push({ query: { ...route.query, tab: id }, hash: route.hash });
};
watch([activeTab, isClassic], async () => {
  // Also refresh measurements on browser Back/Forward and cross-tab preference changes.
  await nextTick();
  window.dispatchEvent(new Event("resize"));
});
</script>

<template>
  <div
    v-if="!reference.isLoading"
    class="reference-workspace w-full min-w-0 space-y-5 2xl:space-y-6"
    :class="{ 'reference-workspace-new': !isClassic }"
    :data-layout="isClassic ? 'classic' : 'new'"
  >
    <ReferenceHeader :classic="isClassic" />

    <ReferenceTabNavigation
      v-if="!isClassic"
      :tabs="tabs"
      :active-tab="activeTab"
      @select="selectTab"
    />

    <div v-if="isClassic" class="2xl:hidden">
      <ReferenceClassicNavigation />
    </div>

    <div
      class="grid min-w-0 gap-6"
      :class="{
        '2xl:grid-cols-[minmax(0,1fr)_clamp(18rem,15vw,22rem)]': isClassic,
      }"
    >
      <div
        id="reference-tab-panel"
        class="reference-section-grid grid min-w-0 items-start gap-6 2xl:grid-cols-12"
        :role="isClassic ? undefined : 'tabpanel'"
        :aria-label="isClassic ? undefined : activeTabLabel"
        :tabindex="isClassic ? undefined : 0"
      >
        <ReferenceProgressSection v-show="showTab('overview')" />

        <ReferenceOverviewSections v-show="showTab('overview')" />

        <ReferenceJourneySection v-show="showTab('schedule')" />

        <ReferenceScheduleSection v-show="showTab('schedule')" />

        <ReferenceAllocationSection v-show="showTab('resources')" />

        <ReferencePayrollSection
          v-show="showTab('payroll')"
          :responsive="!isClassic"
        />

        <ReferenceTextSection
          v-show="showTab('overview')"
          section-id="description"
          title="Reference Description"
          label="Description"
          placeholder="Description not provided"
        />

        <ReferenceTextSection
          v-show="showTab('overview')"
          section-id="notes"
          title="Internal Notes"
          label="Notes"
          placeholder="Notes not provided"
        />

        <ReferenceActivitySections v-show="showTab('activity')" />
      </div>

      <aside
        v-if="isClassic"
        class="hidden h-fit space-y-4 2xl:sticky 2xl:top-4 2xl:block"
      >
        <ReferenceClassicNavigation sidebar />
      </aside>
    </div>
  </div>

  <div v-else>Loading..</div>

  <ReferenceDialogs />
</template>

<style scoped>
.reference-workspace {
  container: reference-workspace / inline-size;
}
.reference-workspace-new :deep(.reference-text-content) {
  max-width: 75ch;
  overflow-wrap: anywhere;
}
.reference-workspace-new .reference-section-grid {
  grid-template-columns: minmax(0, 1fr);
}
.reference-workspace-new .reference-section-grid > * {
  grid-column: 1 / -1;
}
@container reference-workspace (min-width: 60rem) {
  .reference-workspace-new .reference-section-grid {
    grid-template-columns: repeat(12, minmax(0, 1fr));
  }
  .reference-workspace-new :deep(#description),
  .reference-workspace-new :deep(#notes) {
    grid-column: span 6;
  }
  .reference-workspace-new :deep(.reference-activity-grid) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
  .reference-workspace-new :deep(.reference-activity-grid > section) {
    grid-column: auto;
  }
}
</style>
